import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { Tournament, Config, RankingConfig, Team, Player, UserProfile, Club } from '../../types';
import { PublicTournamentCard } from '../tournament/PublicTournamentCard';

interface PublicTournamentViewProps {
  tournament: Tournament;
  config: Config;
  rankingConfig: RankingConfig;
  allTeams: Team[];
  players: Player[];
  allUsers: UserProfile[];
  clubs: Club[];
  sedesPublicas: UserProfile[];
  onRegisterClick: () => void;
  onLoginClick: () => void;
}

export function PublicTournamentView({
  tournament,
  config,
  rankingConfig,
  allTeams,
  players,
  allUsers,
  clubs,
  sedesPublicas,
  onRegisterClick,
  onLoginClick
}: PublicTournamentViewProps) {
  const [missingClubs, setMissingClubs] = useState<UserProfile[]>([]);
  const fetchedIdsRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    const fetchMissingProfiles = async () => {
      const idsToFetch = new Set<string>();
      
      if (tournament.organizerId) {
        idsToFetch.add(tournament.organizerId);
      }
      
      if (tournament.locationMode === 'clubs' && tournament.locationClubIds) {
        tournament.locationClubIds.forEach(id => idsToFetch.add(id));
      }

      // Remove IDs we already have or have already attempted to fetch
      const existingIds = new Set([
        ...sedesPublicas.map(s => s.uid),
        ...allUsers.map(u => u.uid),
        ...missingClubs.map(c => c.uid),
        ...Array.from(fetchedIdsRef.current)
      ]);

      const missingIds = Array.from(idsToFetch).filter(id => !existingIds.has(id));

      if (missingIds.length > 0) {
        // Mark as fetched
        missingIds.forEach(id => fetchedIdsRef.current.add(id));
        
        try {
          const profiles = await Promise.all(
            missingIds.map(async (id) => {
              const docRef = doc(db, getCollectionPath('users'), id);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                return { id: docSnap.id, uid: docSnap.id, ...docSnap.data() } as any as UserProfile;
              }
              return null;
            })
          );
          
          const validProfiles = profiles.filter(p => p !== null) as UserProfile[];
          if (validProfiles.length > 0) {
            setMissingClubs(prev => {
              const newClubs = [...prev];
              validProfiles.forEach(p => {
                if (!newClubs.some(c => c.uid === p.uid)) {
                  newClubs.push(p);
                }
              });
              return newClubs;
            });
          }
        } catch (error) {
          console.error("Error fetching missing profiles:", error);
        }
      }
    };
    
    fetchMissingProfiles();
  }, [tournament.organizerId, tournament.locationMode, tournament.locationClubIds, sedesPublicas, allUsers, missingClubs]);

  // Override onPlayerClick to show registration prompt
  const handlePlayerClick = (id: string | null) => {
    onRegisterClick();
  };

  const handleShareCanvas = () => {
    // Canvas sharing disabled or handled differently for guests
  };

  return (
    <div className="animate-in fade-in duration-500 w-full flex flex-col items-center py-4 md:py-8 mb-24">
      <div className="w-full max-w-4xl relative">
        <PublicTournamentCard
          index={0}
          tournament={tournament}
          config={config}
          rankingConfig={rankingConfig}
          allTeams={allTeams}
          players={players}
          allUsers={allUsers}
          clubs={clubs}
          sedesPublicas={[...sedesPublicas, ...missingClubs]}
          onPlayerClick={handlePlayerClick}
          onJoin={() => onRegisterClick()}
          onCancelJoin={() => {}}
          onOpenChat={() => onRegisterClick()}
          handleShareCanvas={handleShareCanvas}
          hideShareButton={true}
          hideLocation={true}
          isExpanded={true}
          onToggle={() => {}}
        />

        {/* Inline Call To Action */}
        <div className="mt-8 mb-4 px-4 flex justify-center w-full">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl max-w-md w-full flex flex-col items-center text-center gap-5">
            <p className="text-sm text-white font-bold tracking-tight">
              🎾 ¿Querés participar o seguir más torneos?
            </p>
            
            <div className="flex flex-col gap-3 w-full">
              {/* Primary CTA */}
              <button
                onClick={onRegisterClick}
                className="relative group w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-95 text-black"
                style={{ 
                  background: config.primaryColor || '#eab308', 
                  boxShadow: `0 0 20px ${(config.primaryColor || '#eab308')}50, 0 4px 15px rgba(0,0,0,0.3)` 
                }}
              >
                <span className="absolute inset-0 translate-x-[-110%] group-hover:translate-x-[110%] transition-transform duration-700 ease-out"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', skewX: '-15deg' }} />
                ✦ Crear mi Perfil Gratis
              </button>

              {/* Secondary CTA */}
              <button
                onClick={onLoginClick}
                className="w-full py-4 rounded-2xl font-bold text-xs border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all duration-300 backdrop-blur-sm bg-white/5 active:scale-95 uppercase tracking-widest"
              >
                Ya tengo cuenta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
